import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, MessageCircle, UserPlus, Image as ImageIcon, Inbox } from 'lucide-react';
import { getNotifications, markNotificationsSeen } from '../api/notifications';

function Notifications({ onViewed }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const data = await getNotifications();
      setNotifications(data.items || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Mark notifications as seen when page opens
    markNotificationsSeen().catch(() => {});
    if (onViewed) onViewed();
  }, [fetchNotifications, onViewed]);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getIcon = (type) => {
    switch (type) {
      case 'photo_verified':
      case 'new_photo':
        return <Camera size={18} color="#fff" />;
      case 'new_message':
        return <MessageCircle size={18} color="#fff" />;
      case 'access_request':
        return <UserPlus size={18} color="#fff" />;
      default:
        return <ImageIcon size={18} color="#fff" />;
    }
  };

  const getIconBg = (type) => {
    switch (type) {
      case 'photo_verified':
      case 'new_photo':
        return '#14b8a6';
      case 'new_message':
        return '#3b82f6';
      case 'access_request':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const handlePress = (item) => {
    if (item.photo_id) {
      navigate(`/photo/${item.photo_id}`);
    }
  };

  if (loading) {
    return (
      <div className="page-container" data-testid="notifications-loading">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="notifications-page">
      <div style={{ padding: '1rem 1rem 0.75rem', borderBottom: '1px solid hsl(var(--border))' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: 'hsl(var(--foreground))' }}>
          Inbox
        </h1>
      </div>

      {notifications.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            padding: '1.5rem',
            textAlign: 'center',
          }}
          data-testid="notifications-empty"
        >
          <Inbox size={48} color="#d1d5db" />
          <p style={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280', marginTop: '1rem' }}>
            No notifications yet
          </p>
          <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.5rem' }}>
            When someone shares a photo with you, you'll see it here.
          </p>
        </div>
      ) : (
        <div data-testid="notifications-list">
          {notifications.map((item) => {
            const isUnseen = !item.seen_at;
            return (
              <button
                key={item.id}
                onClick={() => handlePress(item)}
                data-testid={`notification-${item.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  padding: '0.875rem 1rem',
                  gap: '0.75rem',
                  borderBottom: '1px solid #f3f4f6',
                  background: isUnseen ? 'rgba(20, 184, 166, 0.04)' : 'transparent',
                  border: 'none',
                  cursor: item.photo_id ? 'pointer' : 'default',
                  textAlign: 'left',
                  transition: 'background 150ms ease',
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: getIconBg(item.type),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {getIcon(item.type)}
                </div>

                {/* Body */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      fontSize: '0.875rem',
                      color: isUnseen ? '#111827' : '#374151',
                      fontWeight: isUnseen ? 600 : 400,
                      lineHeight: 1.4,
                      margin: 0,
                    }}
                  >
                    {item.body}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', margin: '0.125rem 0 0' }}>
                    {formatTimeAgo(item.created_at)}
                  </p>
                </div>

                {/* Thumbnail */}
                {item.photo_url && (
                  <img
                    src={item.photo_url}
                    alt=""
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 6,
                      objectFit: 'cover',
                      backgroundColor: '#f3f4f6',
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Notifications;
